import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fmtPrice } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useBusiness } from "../context/BusinessContext";
import { toast } from "sonner";
import { Minus, Plus } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();
  const { business } = useBusiness();
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => navigate("/shop"));
  }, [id, navigate]);

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center text-sm">Loading...</div>
    );
  }

  const out = product.stock <= 0;

  const handleAdd = () => {
    addItem(product, qty);
    toast.success(`Added to cart: ${product.name}`);
  };

  const handleBuyNow = () => {
    addItem(product, qty);
    navigate("/cart");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="product-detail-page">
      <div className="text-xs uppercase tracking-ultra text-neutral-500 mb-6">
        <Link to="/shop" className="hover:text-black">
          Shop
        </Link>{" "}
        / <span>{product.category_name}</span>
      </div>

      <div className="grid md:grid-cols-2 gap-12">
        <div className="bg-[#f0f0ea] aspect-[4/5] overflow-hidden">
          {product.image_url && (
            <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
          )}
        </div>

        <div className="md:sticky md:top-24 md:self-start">
          <div className="text-[10px] uppercase tracking-ultra text-neutral-500">
            {product.category_name}
          </div>
          <h1 className="mt-2 text-4xl md:text-5xl font-black tracking-tighter leading-none">
            {product.name}
          </h1>
          <div className="mt-6 text-2xl font-medium">
            {fmtPrice(product.price, business.currency)}
          </div>

          <div className="mt-4">
            {out ? (
              <span className="bg-neutral-200 text-neutral-500 px-2 py-1 text-[10px] uppercase tracking-widest line-through" data-testid="stock-badge">
                Sold out
              </span>
            ) : product.stock <= 5 ? (
              <span className="bg-accent text-white px-2 py-1 text-[10px] uppercase tracking-widest" data-testid="stock-badge">
                Only {product.stock} left
              </span>
            ) : (
              <span className="bg-black text-white px-2 py-1 text-[10px] uppercase tracking-widest" data-testid="stock-badge">
                In stock
              </span>
            )}
          </div>

          <p className="mt-8 text-neutral-700 leading-relaxed" data-testid="product-description">
            {product.description}
          </p>

          {!out && (
            <div className="mt-10 flex items-center gap-6">
              <div className="inline-flex items-center border border-black">
                <button
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  className="px-3 py-3 hover:bg-black hover:text-white transition-colors"
                  data-testid="qty-decrement"
                  aria-label="Decrease quantity"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="px-6 text-sm font-bold tabular-nums" data-testid="qty-value">
                  {qty}
                </span>
                <button
                  onClick={() => setQty(Math.min(product.stock, qty + 1))}
                  className="px-3 py-3 hover:bg-black hover:text-white transition-colors"
                  data-testid="qty-increment"
                  aria-label="Increase quantity"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="text-xs uppercase tracking-ultra text-neutral-500">
                / {product.stock} available
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              disabled={out}
              onClick={handleAdd}
              className="bg-black text-white px-8 py-4 text-xs uppercase tracking-ultra font-bold hover:bg-neutral-800 transition-colors disabled:bg-neutral-300 disabled:cursor-not-allowed flex-1"
              data-testid="add-to-cart-button"
            >
              {out ? "Sold out" : "Add to cart"}
            </button>
            <button
              disabled={out}
              onClick={handleBuyNow}
              className="border border-black px-8 py-4 text-xs uppercase tracking-ultra font-bold hover:bg-black hover:text-white transition-colors disabled:opacity-30 flex-1"
              data-testid="buy-now-button"
            >
              Buy now
            </button>
          </div>

          <div className="mt-12 border-t border-black/10 pt-6 text-xs text-neutral-500 uppercase tracking-ultra">
            <div>Secure checkout • Billplz • Stripe • QR</div>
            <div className="mt-2">Free shipping over {business.currency} 250</div>
          </div>
        </div>
      </div>
    </div>
  );
}
